/**
 * useAiPipeline — React hook managing all AI pipeline dashboard state.
 *
 * Uses useReducer with independent section-level loading and error states.
 * Each section (status, dlq, config, metrics, health) fetches independently —
 * a failure in one does not affect the others.
 *
 * Requirements: 1.5, 1.7, 1.8, 9.4
 */

import { useReducer, useEffect, useCallback } from 'react';
import {
  getPipelineStatus,
  getPipelineDlq,
  purgePipelineDlq,
  getPipelineConfig,
  getPipelineMetrics,
  getPipelineHealth,
  retriggerPipeline,
} from '../api/ai-pipeline';
import type {
  AiPipelineState,
  PipelineStatusResponse,
  DlqResponse,
  PipelineConfigResponse,
  PipelineMetricsResponse,
  PipelineHealthResponse,
  RetriggerResponse,
} from '../types/ai-pipeline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SectionKey = 'status' | 'dlq' | 'config' | 'metrics' | 'health';

type AiPipelineAction =
  | { type: 'FETCH_START'; section: SectionKey }
  | { type: 'FETCH_STATUS_SUCCESS'; payload: PipelineStatusResponse }
  | { type: 'FETCH_DLQ_SUCCESS'; payload: DlqResponse }
  | { type: 'FETCH_CONFIG_SUCCESS'; payload: PipelineConfigResponse }
  | { type: 'FETCH_METRICS_SUCCESS'; payload: PipelineMetricsResponse }
  | { type: 'FETCH_HEALTH_SUCCESS'; payload: PipelineHealthResponse }
  | { type: 'FETCH_ERROR'; section: SectionKey; error: string }
  | { type: 'PURGE_DLQ_SUCCESS' }
  | { type: 'RETRIGGER_SUCCESS'; payload: RetriggerResponse };

export interface UseAiPipelineReturn {
  readonly state: AiPipelineState;
  refreshAll: () => void;
  refreshStatus: () => void;
  refreshDlq: () => void;
  purgeDlq: () => Promise<void>;
  retriggerPage: (pageId: string) => Promise<RetriggerResponse>;
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState: AiPipelineState = {
  status: null,
  dlq: null,
  config: null,
  metrics: null,
  health: null,
  isLoading: {
    status: false,
    dlq: false,
    config: false,
    metrics: false,
    health: false,
  },
  errors: {
    status: null,
    dlq: null,
    config: null,
    metrics: null,
    health: null,
  },
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function reducer(state: AiPipelineState, action: AiPipelineAction): AiPipelineState {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        isLoading: { ...state.isLoading, [action.section]: true },
        errors: { ...state.errors, [action.section]: null },
      };

    case 'FETCH_STATUS_SUCCESS':
      return {
        ...state,
        status: action.payload,
        isLoading: { ...state.isLoading, status: false },
      };

    case 'FETCH_DLQ_SUCCESS':
      return {
        ...state,
        dlq: action.payload,
        isLoading: { ...state.isLoading, dlq: false },
      };

    case 'FETCH_CONFIG_SUCCESS':
      return {
        ...state,
        config: action.payload,
        isLoading: { ...state.isLoading, config: false },
      };

    case 'FETCH_METRICS_SUCCESS':
      return {
        ...state,
        metrics: action.payload,
        isLoading: { ...state.isLoading, metrics: false },
      };

    case 'FETCH_HEALTH_SUCCESS':
      return {
        ...state,
        health: action.payload,
        isLoading: { ...state.isLoading, health: false },
      };

    case 'FETCH_ERROR':
      return {
        ...state,
        isLoading: { ...state.isLoading, [action.section]: false },
        errors: { ...state.errors, [action.section]: action.error },
      };

    case 'PURGE_DLQ_SUCCESS':
      return {
        ...state,
        dlq: state.dlq
          ? { ...state.dlq, approximateCount: 0, messages: [] }
          : null,
      };

    case 'RETRIGGER_SUCCESS':
      return state;

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAiPipeline(): UseAiPipelineReturn {
  const [state, dispatch] = useReducer(reducer, initialState);

  const refreshStatus = useCallback(async () => {
    dispatch({ type: 'FETCH_START', section: 'status' });
    try {
      const data = await getPipelineStatus();
      dispatch({ type: 'FETCH_STATUS_SUCCESS', payload: data });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch pipeline status';
      dispatch({ type: 'FETCH_ERROR', section: 'status', error: message });
    }
  }, []);

  const refreshDlq = useCallback(async () => {
    dispatch({ type: 'FETCH_START', section: 'dlq' });
    try {
      const data = await getPipelineDlq();
      dispatch({ type: 'FETCH_DLQ_SUCCESS', payload: data });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch DLQ data';
      dispatch({ type: 'FETCH_ERROR', section: 'dlq', error: message });
    }
  }, []);

  const refreshConfig = useCallback(async () => {
    dispatch({ type: 'FETCH_START', section: 'config' });
    try {
      const data = await getPipelineConfig();
      dispatch({ type: 'FETCH_CONFIG_SUCCESS', payload: data });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch pipeline config';
      dispatch({ type: 'FETCH_ERROR', section: 'config', error: message });
    }
  }, []);

  const refreshMetrics = useCallback(async () => {
    dispatch({ type: 'FETCH_START', section: 'metrics' });
    try {
      const data = await getPipelineMetrics();
      dispatch({ type: 'FETCH_METRICS_SUCCESS', payload: data });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch pipeline metrics';
      dispatch({ type: 'FETCH_ERROR', section: 'metrics', error: message });
    }
  }, []);

  const refreshHealth = useCallback(async () => {
    dispatch({ type: 'FETCH_START', section: 'health' });
    try {
      const data = await getPipelineHealth();
      dispatch({ type: 'FETCH_HEALTH_SUCCESS', payload: data });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch pipeline health';
      dispatch({ type: 'FETCH_ERROR', section: 'health', error: message });
    }
  }, []);

  const refreshAll = useCallback(() => {
    refreshStatus();
    refreshDlq();
    refreshConfig();
    refreshMetrics();
    refreshHealth();
  }, [refreshStatus, refreshDlq, refreshConfig, refreshMetrics, refreshHealth]);

  const purgeDlq = useCallback(async () => {
    try {
      await purgePipelineDlq();
      dispatch({ type: 'PURGE_DLQ_SUCCESS' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to purge DLQ';
      dispatch({ type: 'FETCH_ERROR', section: 'dlq', error: message });
    }
  }, []);

  const retriggerPage = useCallback(async (pageId: string): Promise<RetriggerResponse> => {
    const result = await retriggerPipeline(pageId);
    dispatch({ type: 'RETRIGGER_SUCCESS', payload: result });
    return result;
  }, []);

  // Fetch all sections on mount
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  return {
    state,
    refreshAll,
    refreshStatus,
    refreshDlq,
    purgeDlq,
    retriggerPage,
  };
}
